import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { TasksService } from '../../api/generated/services/TasksService';
import type { AssignableUserView } from '../../api/generated/models/AssignableUserView';
import type { TaskItemView } from '../../api/generated/models/TaskItemView';
import { TaskCreateRequest } from '../../api/generated/models/TaskCreateRequest';
import type { TaskPatchRequest } from '../../api/generated/models/TaskPatchRequest';
import { setError } from './errorSlice';
import { addNotification } from './notificationSlice';
import { startLoading, stopLoading } from './loadingSlice';
import { helper } from '../../utils/helper';
import type { RootState } from '../store';

interface TaskState {
  items: TaskItemView[];
  selectedTask: TaskItemView | null;
  assignableUsers: AssignableUserView[];
  assignableUsersLoaded: boolean;
  loading: boolean;
  detailLoading: boolean;
  updateLoading: boolean;
  error: string | null;
  isLoaded: boolean;
}

const initialState: TaskState = {
  items: [],
  selectedTask: null,
  assignableUsers: [],
  assignableUsersLoaded: false,
  loading: false,
  detailLoading: false,
  updateLoading: false,
  error: null,
  isLoaded: false,
};

const notifyError = (dispatch: (action: unknown) => void, err: unknown, fallback: string) => {
  const errorMessage = err instanceof Error ? err.message : fallback;
  dispatch(setError(errorMessage));
  dispatch(addNotification({
    type: 'error',
    title: i18next.t('notification_error'),
    message: errorMessage,
  }));
  return errorMessage;
};

export const fetchAssignableUsers = createAsyncThunk(
  'tasks/fetchAssignableUsers',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    if (state.tasks.assignableUsersLoaded) {
      return state.tasks.assignableUsers;
    }
    try {
      const response = await TasksService.getAppTasksAssignableUsers();
      return response.items ?? [];
    } catch (err) {
      notifyError(dispatch, err, 'Failed to fetch assignable users');
      throw err;
    }
  },
);

export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (_, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await TasksService.getAppTasksList();
      return response.items ?? [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch tasks';
      dispatch(setError(errorMessage));

      if (err instanceof Error && helper.isNotFoundError(err)) {
        return [];
      }

      dispatch(addNotification({
        type: 'error',
        title: i18next.t('notification_error'),
        message: errorMessage,
      }));
      throw err;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  'tasks/fetchTaskById',
  async (taskId: string, { dispatch }) => {
    try {
      const response = await TasksService.getAppTaskGet(taskId);
      return response.item;
    } catch (err) {
      notifyError(dispatch, err, 'Failed to fetch task');
      throw err;
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (
    payload: { title: string; description?: string | null; status?: TaskCreateRequest.status },
    { dispatch }
  ) => {
    try {
      const response = await TasksService.postAppTasksCreate({
        title: payload.title,
        description: payload.description ?? null,
        status: payload.status,
      });
      dispatch(addNotification({
        type: 'success',
        title: i18next.t('notification_success'),
        message: i18next.t('tasks_created_success'),
      }));
      return response.item;
    } catch (err) {
      notifyError(dispatch, err, 'Failed to create task');
      throw err;
    }
  }
);

export const patchTask = createAsyncThunk(
  'tasks/patchTask',
  async (
    { taskId, ...body }: { taskId: string } & TaskPatchRequest,
    { dispatch }
  ) => {
    try {
      const response = await TasksService.patchAppTaskPatch(taskId, body);
      return response.item;
    } catch (err) {
      notifyError(dispatch, err, 'Failed to update task');
      throw err;
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId: string, { dispatch }) => {
    try {
      await TasksService.deleteAppTaskDelete(taskId);
      dispatch(addNotification({
        type: 'success',
        title: i18next.t('notification_success'),
        message: i18next.t('tasks_deleted_success'),
      }));
      return taskId;
    } catch (err) {
      notifyError(dispatch, err, 'Failed to delete task');
      throw err;
    }
  }
);

const upsertTaskItem = (items: TaskItemView[], updated: TaskItemView) => {
  const index = items.findIndex((item) => item.id === updated.id);
  if (index >= 0) {
    items[index] = updated;
    return;
  }
  items.push(updated);
};

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearSelectedTask: (state) => {
      state.selectedTask = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignableUsers.fulfilled, (state, action) => {
        state.assignableUsers = action.payload;
        state.assignableUsersLoaded = true;
      })
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.isLoaded = true;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tasks';
        state.isLoaded = true;
      })
      .addCase(fetchTaskById.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedTask = action.payload;
        upsertTaskItem(state.items, action.payload);
      })
      .addCase(fetchTaskById.rejected, (state) => {
        state.detailLoading = false;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        // List is createdAt DESC; a new task is the newest.
        state.items.unshift(action.payload);
      })
      .addCase(patchTask.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(patchTask.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.selectedTask = action.payload;
        upsertTaskItem(state.items, action.payload);
      })
      .addCase(patchTask.rejected, (state) => {
        state.updateLoading = false;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
        if (state.selectedTask?.id === action.payload) {
          state.selectedTask = null;
        }
      });
  },
});

export const { clearSelectedTask } = taskSlice.actions;
export default taskSlice.reducer;
